import { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CustomerTable from "./CustomerTable";
import AddCustomerModal from "./AddCustomerModal";
import DeleteUserModal from "./DeleteUserModal";

import { fetchCustomersByBranch } from "../api/customersApi";
import { UserSessionContext } from "../../../session/UserSessionContext";
import { useNavigation } from "../../../contexts/NavigationContext";
import useAddCustomer from "../hooks/useAddCustomer";
import useEditCustomer from "../hooks/useEditCustomer";
import useDeleteUser from "../hooks/useDeleteUser";
import { canDeleteUser } from "../domain/deleteUserRules";

export default function CustomersPage() {
  const navigate = useNavigate();
  const { userSession } = useContext(UserSessionContext) || {};
  const { selectedBranchId, selectedBranchName, selectedCustomerEmail, setSelectedCustomer } = useNavigation();

  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const accessToken = userSession?.auth?.accessToken || sessionStorage.getItem("accessToken");
  const jobTitle = userSession?.user?.jobTitle || sessionStorage.getItem("jobTitle");
  const userId = userSession?.user?.id || sessionStorage.getItem("UserID") || "";
  const canEdit = jobTitle === "Organization Admin" || jobTitle === "Branch Admin";
  const canDelete = canDeleteUser({ callerJobTitle: jobTitle, targetRole: "customer" });

  useEffect(() => {
    if (!selectedBranchId) {
      navigate("/branchhomepage");
    }
  }, [selectedBranchId, navigate]);

  const loadCustomers = useCallback(async () => {
    if (!selectedBranchId || !accessToken) return;

    setIsLoading(true);
    setErrorMessage("");

    try {
      const customersList = await fetchCustomersByBranch({
        accessToken,
        branchId: selectedBranchId,
        userId,
      });
      setCustomers(customersList);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      setErrorMessage("Unable to load customers right now.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedBranchId, userId]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const {
    showModal,
    openModal,
    closeModal,
    formData,
    errors,
    submitError,
    isSubmitting,
    handleChange,
    handleSubmit,
  } = useAddCustomer({
    accessToken,
    userId,
    branchId: selectedBranchId,
    onSuccess: loadCustomers,
  });

  const {
    showModal: showEditModal,
    openModal: openEditModal,
    closeModal: closeEditModal,
    formData: editFormData,
    errors: editErrors,
    submitError: editSubmitError,
    isSubmitting: isEditSubmitting,
    handleChange: handleEditChange,
    handleSubmit: handleEditSubmit,
  } = useEditCustomer({
    accessToken,
    userId,
    onSuccess: loadCustomers,
  });

  const {
    target: deleteTarget,
    kind: deleteKind,
    isDeleting,
    openDelete,
    closeDelete,
    confirmDelete,
  } = useDeleteUser({
    accessToken,
    userId,
    onSuccess: loadCustomers,
  });

  const handleSelect = (customer) => {
    setSelectedCustomer(customer.email, customer.first_name);
    navigate("/microgrid-page-design");
  };

  const handleEdit = (customer) => {
    openEditModal(customer);
  };

  const handleDelete = (customer) => {
    openDelete(customer, "customer");
  };

  if (!selectedBranchId) {
    return null;
  }

  if (isLoading) {
    return <p>Loading customers...</p>;
  }

  if (errorMessage) {
    return <p>{errorMessage}</p>;
  }

  return (
    <>
      <CustomerTable
        customers={customers}
        branchName={selectedBranchName}
        activeCustomerEmail={selectedCustomerEmail}
        onSelect={handleSelect}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAddCustomer={openModal}
        canEdit={canEdit}
        canDelete={canDelete}
      />
      <AddCustomerModal
        open={showModal}
        onClose={closeModal}
        formData={formData}
        errors={errors}
        submitError={submitError}
        isSubmitting={isSubmitting}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />
      <AddCustomerModal
        mode="edit"
        open={showEditModal}
        onClose={closeEditModal}
        formData={editFormData}
        errors={editErrors}
        submitError={editSubmitError}
        isSubmitting={isEditSubmitting}
        handleChange={handleEditChange}
        handleSubmit={handleEditSubmit}
      />
      <DeleteUserModal
        open={!!deleteTarget}
        user={deleteTarget}
        kind={deleteKind}
        onConfirm={confirmDelete}
        onClose={closeDelete}
        loading={isDeleting}
      />
    </>
  );
}
